import { z } from 'zod';
import { ORDER_TYPE } from '@lib/types/orderType';
import { filterAllowedFontFamilyTokens } from '@widgets-connect/old-theme-checks';
import {
    WIDGET_THEME_CSS_VAR_NAMES,
    type ParentResponses,
    type WidgetThemeCssVarName
} from '@widgets-connect/message/message.types';

/** DOM / opaque ids from parent (alphanumeric, underscore, hyphen). */
const safeDomId = z
    .string()
    .min(1)
    .max(256)
    .regex(/^[a-zA-Z0-9_-]+$/);

const optionalSafeId = z
    .string()
    .min(1)
    .max(256)
    .regex(/^[a-zA-Z0-9_-]+$/)
    .optional();

const orderTypeEnum = z.enum(ORDER_TYPE);

export function parentInfoResultSchema(messageOrigin: string) {
    return z
        .object({
            websiteId: safeDomId,
            deliveryteamId: optionalSafeId,
            storeId: optionalSafeId,
            orderType: orderTypeEnum.optional(),
            parentUrl: z.string().min(1).max(8192),
            iframeId: safeDomId,
            isChannelRequired: z.boolean(),
            isSingleTimePropOnlyChannel: z.boolean(),
            dataLayerIframeId: safeDomId
        })
        .strict()
        .superRefine((data, ctx) => {
            let parsedUrl: URL;
            try {
                parsedUrl = new URL(data.parentUrl);
            } catch {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Invalid parent URL',
                    path: ['parentUrl']
                });
                return;
            }
            if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Parent URL must use http or https',
                    path: ['parentUrl']
                });
                return;
            }
            if (parsedUrl.origin !== messageOrigin) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Parent URL origin does not match message origin',
                    path: ['parentUrl']
                });
            }
        });
}

const newAccessoryLayerResultSchema = z
    .object({
        iframeId: safeDomId
    })
    .strict();

const parentDocTitleResultSchema = z.string().max(10_000);
const hslTripleSchema = z.string().regex(/^\d{1,3}(\.\d+)?\s+\d{1,3}(\.\d+)?%\s+\d{1,3}(\.\d+)?%$/).max(120);
const radiusSchema = z.string().regex(/^\d{1,3}(\.\d+)?(px|rem|em|%)$/).max(120);
// Keep allowlisted tokens from parent stacks (incl. computed body fonts).
// Returns undefined when nothing survives so the rest of the theme still applies.
const fontFamilySchema = z
    .string()
    .max(200)
    .transform((value) => {
        const filtered = filterAllowedFontFamilyTokens(value);
        if (!filtered) {
            console.warn(`[widget-theme] dropping non-allowlisted fontFamily: ${value}`);
            return undefined;
        }
        return filtered;
    });

const colorVarNameSet = new Set<WidgetThemeCssVarName>(WIDGET_THEME_CSS_VAR_NAMES.filter((name) => name !== '--radius'));

// Silently drop unknown/invalid CSS vars instead of rejecting the whole payload.
// A single malformed value from a host site must NOT prevent colorMode/fontFamily
// from reaching the iframe.
const themeVariablesSchema = z
    .partialRecord(z.enum(WIDGET_THEME_CSS_VAR_NAMES), z.string().max(120))
    .transform((vars) => {
        const filtered: Partial<Record<WidgetThemeCssVarName, string>> = {};
        for (const [name, value] of Object.entries(vars) as [WidgetThemeCssVarName, string][]) {
            const isValid = colorVarNameSet.has(name)
                ? hslTripleSchema.safeParse(value).success
                : radiusSchema.safeParse(value).success;
            if (isValid) {
                filtered[name] = value;
            } else {
                console.warn(`[widget-theme] dropping invalid CSS value for ${name}: ${value}`);
            }
        }
        return filtered;
    });

const parentThemeVarsResultSchema = z
    .object({
        variables: themeVariablesSchema,
        fontFamily: fontFamilySchema.optional(),
        colorMode: z.enum(['light', 'dark']).optional()
    })
    .strict();

type ParentResponseAction = keyof ParentResponses;

export function parseParentResponseResult<A extends ParentResponseAction>(
    action: A,
    result: unknown,
    messageOrigin: string
):
    | { success: true; data: ParentResponses[A] }
    | { success: false; error: z.ZodError } {
    switch (action) {
        case 'zmgNewAccessoryLayerWidget': {
            const parsed = newAccessoryLayerResultSchema.safeParse(result);
            return parsed.success
                ? { success: true, data: parsed.data as ParentResponses[A] }
                : { success: false, error: parsed.error };
        }
        case 'zmgGetParentInfo': {
            const parsed = parentInfoResultSchema(messageOrigin).safeParse(result);
            return parsed.success
                ? { success: true, data: parsed.data as ParentResponses[A] }
                : { success: false, error: parsed.error };
        }
        case 'zmgGetParentDocTitle': {
            const parsed = parentDocTitleResultSchema.safeParse(result);
            return parsed.success
                ? { success: true, data: parsed.data as ParentResponses[A] }
                : { success: false, error: parsed.error };
        }
        case 'zmgGetParentThemeVars': {
            const parsed = parentThemeVarsResultSchema.safeParse(result);
            return parsed.success
                ? { success: true, data: parsed.data as ParentResponses[A] }
                : { success: false, error: parsed.error };
        }
        default: {
            const _exhaustive: never = action;
            return _exhaustive;
        }
    }
}
